import { z } from "zod";
import { BudgetSchema } from "./agent.js";
import { TimestampSchema } from "./common.js";
import { IdSchema } from "./ids.js";
import { ConnectionModelPolicySchema } from "./model-policy.js";
/**
 * Credentials are always references into the secret vault, never raw values.
 * `subscription_oauth` connections are bound to `ownerUserId` (DESIGN §6 binding rule).
 */
export const ConnectionCredentialSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("api_key"), secretRef: IdSchema }),
    z.object({
        kind: z.literal("subscription_oauth"),
        secretRef: IdSchema,
        ownerUserId: IdSchema,
        /** Per-connection engine config dir under $UZVERSE_DATA (mode 0700). */
        configDir: z.string().min(1),
    }),
]);
export const ConnectionStatusSchema = z.enum(["ok", "warning", "cooling", "disabled"]);
/**
 * Persisted credential health, separate from the transient `status` (rate limits, cooling):
 * `needs_relogin` is set when the provider rejected the credential (engine run or test) and
 * cleared by a successful re-login; `disabled` is an operator switch.
 */
export const ConnectionAuthStateSchema = z.enum(["active", "needs_relogin", "disabled"]);
export const ConnectionVisibilitySchema = z.enum(["company", "public"]);
/** Per-company starting values of the copies of a public connection. */
export const PublicDefaultsSchema = z.object({
    budget: BudgetSchema,
    /** Parallel runs one company's copy may hold. Absent = unlimited. */
    maxConcurrent: z.number().int().min(1).max(1000).optional(),
    priority: z.number().int().min(-1000).max(1000),
});
/**
 * Company setting `engine.connections.policy`: how a company orders its own connections against
 * the public ones. `own_only` opts the company out of public connections.
 */
export const ConnectionPolicySchema = z.enum(["own_first", "public_first", "own_only"]);
export const CONNECTION_POLICY_SETTING = "engine.connections.policy";
export const DEFAULT_CONNECTION_POLICY = "own_first";
export const ConnectionSchema = z.object({
    id: IdSchema,
    companyId: IdSchema,
    /** AgentEngine id, e.g. "claude-agent-sdk". */
    engine: z.string().min(1),
    label: z.string().min(1),
    credential: ConnectionCredentialSchema,
    /** Higher runs first. */
    priority: z.number().int(),
    budget: BudgetSchema,
    status: ConnectionStatusSchema,
    coolingUntil: TimestampSchema.optional(),
    /** Absent = `active`. */
    authState: ConnectionAuthStateSchema.optional(),
    /** When `authState` last changed. */
    authStateAt: TimestampSchema.optional(),
    /** Last error that changed `authState` (provider message, never a secret). */
    lastError: z.string().max(500).optional(),
    /** Allowed models, default model, effort, concurrency, daily cap. Absent = defaults. */
    modelPolicy: ConnectionModelPolicySchema.optional(),
    /**
     * Set on a copy that the Owner shared from another company: the source connection and its
     * company. The credential follows the source (re-login, secret replacement, disabling); the
     * budget and priority stay per company copy.
     */
    sharedFromConnectionId: IdSchema.optional(),
    sharedFromCompanyId: IdSchema.optional(),
    /**
     * `public` (Owner only, set on the source) makes the connection available to every company:
     * the Owner API keeps a linked copy in each company node. Absent = `company`. For a public
     * subscription connection the Owner's toggle lifts the owner-user binding.
     */
    visibility: ConnectionVisibilitySchema.optional(),
    /** Set on a source with `visibility: public`: what a new per-company copy starts with. */
    publicDefaults: PublicDefaultsSchema.optional(),
    /** True on a linked copy that was created because its source is public. */
    publicCopy: z.boolean().optional(),
});
/** Safe view of a connection for UIs and the owner panel: no secret ref, only a fingerprint. */
export const ConnectionSummarySchema = ConnectionSchema.omit({ credential: true }).extend({
    credentialKind: z.enum(["api_key", "subscription_oauth"]),
    ownerUserId: IdSchema.optional(),
    fingerprint: z.string().min(1),
});
/** A public source, or the copy of one. The Owner's toggle lifts the subscription binding. */
export function isPublicConnection(c) {
    return c.visibility === "public" || c.publicCopy === true;
}
/**
 * The subscription binding rule (hard invariant): a `subscription_oauth` connection may only run
 * work done on behalf of its owning user, unless the Owner made it public. API-key connections
 * may serve anyone in the company.
 */
export function connectionMayServe(connection, onBehalfOf) {
    if (isPublicConnection(connection))
        return true;
    if (connection.credential.kind === "subscription_oauth") {
        return onBehalfOf !== null && onBehalfOf === connection.credential.ownerUserId;
    }
    return true;
}
/** True when the connection may be picked: credential healthy and not operator-disabled. */
export function connectionUsable(c) {
    return (c.authState ?? "active") === "active";
}
/** True when the connection's policy allows `model` (no list = all models). */
export function connectionAllowsModel(connection, model) {
    const allowed = connection.modelPolicy?.allowedModels;
    return !allowed || allowed.length === 0 || allowed.includes(model);
}
//# sourceMappingURL=connection.js.map